import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtMs, now, clamp } from '@/utils/format';
import { qualityFromTime, scoreFromTime } from '@/utils/scoring';

interface Props { autoRun: boolean; autoNonce: number; }

export function ComputeTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [fibN, setFibN] = useState(40);
  const [sortN, setSortN] = useState(700000);
  const [primeLimit, setPrimeLimit] = useState(2000000);
  const [metrics, setMetrics] = useState({
    fibSlowMs: null as number | null,
    fibMemoMs: null as number | null,
    sortMs: null as number | null,
    primeMs: null as number | null,
    totalMs: null as number | null,
    status: 'idle',
  });
  const abortRef = useRef(false);
  const autoDoneRef = useRef(false);

  function reset() {
    abortRef.current = true;
    autoDoneRef.current = false;
    setRunning(false);
    setMetrics({ fibSlowMs: null, fibMemoMs: null, sortMs: null, primeMs: null, totalMs: null, status: 'idle' });
  }

  function fibSlow(n: number): number {
    if (n <= 1) return n;
    return fibSlow(n - 1) + fibSlow(n - 2);
  }

  function fibMemo(n: number, memo: Record<number, number>): number {
    if (n <= 1) return n;
    if (memo[n] != null) return memo[n];
    memo[n] = fibMemo(n - 1, memo) + fibMemo(n - 2, memo);
    return memo[n];
  }

  function tick() { return new Promise<void>((r) => setTimeout(r, 0)); }

  function doReport(total: number) {
    const sc = Math.round(scoreFromTime(total, 2500, 6000));
    const testId: TestId = 'tv-compute';
    reportResult(testId, { score: sc, primary: `Total ${fmtMs(total)}`, details: metrics });
    if (autoRun && !autoDoneRef.current) {
      autoDoneRef.current = true;
      onAutoDone(testId);
    }
  }

  async function run() {
    abortRef.current = false;
    autoDoneRef.current = false;
    setRunning(true);
    setMetrics((m) => ({ ...m, status: 'running' }));
    const tStart = now();

    // Fib slow
    const n1 = clamp(parseInt(String(fibN), 10) || 40, 35, 44);
    const t0 = now();
    fibSlow(n1);
    const fibSlowTime = now() - t0;
    if (abortRef.current) return;
    setMetrics((m) => ({ ...m, fibSlowMs: fibSlowTime, status: 'fib slow done' }));

    await tick();
    if (abortRef.current) return;

    // Fib memo
    const n2 = clamp((parseInt(String(fibN), 10) || 40) + 2, 35, 46);
    const t1 = now();
    fibMemo(n2, {});
    const fibMemoTime = now() - t1;
    if (abortRef.current) return;
    setMetrics((m) => ({ ...m, fibMemoMs: fibMemoTime, status: 'fib memo done' }));

    await tick();
    if (abortRef.current) return;

    // Sort
    const sn = clamp(parseInt(String(sortN), 10) || 700000, 500000, 1000000);
    let seed = 123456789;
    function rnd() { seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5; return (seed >>> 0) / 4294967295; }
    const base = new Array(sn);
    for (let i = 0; i < sn; i++) base[i] = rnd();
    const t2 = now();
    for (let r = 0; r < 3; r++) {
      if (abortRef.current) return;
      const arr = base.slice();
      arr.sort((a, b) => a - b);
    }
    const sortTime = now() - t2;
    if (abortRef.current) return;
    setMetrics((m) => ({ ...m, sortMs: sortTime, status: 'sorting done' }));

    await tick();
    if (abortRef.current) return;

    // Primes
    const limit = clamp(parseInt(String(primeLimit), 10) || 2000000, 500000, 5000000);
    const t3 = now();
    const sieve = new Uint8Array(limit + 1);
    let primeCount = 0;
    for (let i = 2; i <= limit; i++) {
      if (sieve[i] === 0) {
        primeCount++;
        if (i * i <= limit) for (let j = i * i; j <= limit; j += i) sieve[j] = 1;
      }
    }
    const primeTime = now() - t3;
    if (abortRef.current) return;
    setMetrics((m) => ({ ...m, primeMs: primeTime, status: `done (${primeCount} primes)` }));

    const total = now() - tStart;
    setMetrics((m) => ({ ...m, totalMs: total, status: 'done' }));
    setRunning(false);
    doReport(total);
  }

  function stopAndScore() {
    abortRef.current = true;
    setRunning(false);
    doReport(metrics.totalMs ?? 99999);
  }

  useEffect(() => {
    if (!autoRun) return;
    setFibN(40);
    setSortN(700000);
    setPrimeLimit(2000000);
    run();
  }, [autoRun, autoNonce]);

  const qTotal = qualityFromTime(metrics.totalMs, 2500, 6000);

  return (
    <TestCard title={t('tv.compute.title', language)} description={t('tv.compute.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={run} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stopAndScore} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.fibN', language)}</div>
          <input value={fibN} disabled={running || autoRun} onChange={(e) => setFibN(Number(e.target.value))} inputMode="numeric" className="input w-20" />
        </div>
        <div className="field">
          <div className="label">{t('label.sortN', language)}</div>
          <input value={sortN} disabled={running || autoRun} onChange={(e) => setSortN(Number(e.target.value))} inputMode="numeric" className="input w-28" />
        </div>
        <div className="field">
          <div className="label">{t('label.primeLimit', language)}</div>
          <input value={primeLimit} disabled={running || autoRun} onChange={(e) => setPrimeLimit(Number(e.target.value))} inputMode="numeric" className="input w-28" />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label="Fib (recursive)" value={fmtMs(metrics.fibSlowMs)} hint="Worst-case" />
        <MetricBox label="Fib (memo)" value={fmtMs(metrics.fibMemoMs)} hint="Better algo" />
        <MetricBox label="Sorting" value={fmtMs(metrics.sortMs)} hint="3 rounds" />
        <MetricBox label="Primes" value={fmtMs(metrics.primeMs)} hint="Sieve" />
      </div>

      <div className="h-px bg-border my-3.5" />
      <div className="text-xs">
        <span className="font-mono">Status: </span>
        <span className="font-mono text-white">{metrics.status}</span>
        {' · '}
        <span className={`font-black ${qTotal.cls}`}>Overall: {qTotal.label}</span>
        {' · Total: '}{fmtMs(metrics.totalMs)}
      </div>
    </TestCard>
  );
}
